import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchOperationalChannel, type OperationalChannelData } from "@rekonas/data-access";
import { type ViewerDataSource } from "@rekonas/signal-viewer";
import { Alert, Button, Spinner } from "@rekonas/ui";

const PREVIEW_SAMPLES = 32;
type Preview = Omit<OperationalChannelData, "samples"> & { values: string; bytes: number; elapsedMs: number };

/** Temporary Operational handoff: replace this panel with the spectrogram consumer. */
export function OperationalBulkChannelPanel({ source }: { source: Pick<ViewerDataSource, "key" | "recordingRef" | "loadCapabilities"> }) {
  const capabilities = useQuery({
    queryKey: [...source.key, "capabilities"],
    queryFn: ({ signal }) => source.loadCapabilities(signal),
  });
  const channels = (capabilities.data?.modalities || [])
    .filter((modality) => modality.state === "available" && !modality.annotation)
    .flatMap((modality) => modality.channels);
  const preferred = channels.find((channel) => channel.id === "H1_EEG1" || channel.label === "H1_EEG1") || channels[0];
  const [selection, setSelection] = useState("");
  const selector = channels.some((channel) => channel.id === selection) ? selection : preferred?.id || "";
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => { request.current?.abort(); }, []);

  async function fetchChannel() {
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError("");
    setPreview(null);
    const start = performance.now();
    try {
      const { samples, ...metadata } = await fetchOperationalChannel(source.recordingRef, selector, controller.signal);
      const elapsedMs = performance.now() - start;
      if (controller.signal.aborted) return;
      setPreview({ ...metadata, bytes: samples.byteLength, elapsedMs,
        values: Array.from(samples.subarray(0, PREVIEW_SAMPLES), (value) => String(value)).join(", ") });
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Full channel fetch failed.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  return <section className="operational-report-bridge" aria-label="Temporary bulk channel handoff">
    <h2>Full channel · temporary handoff</h2>
    <p>Fetch one full-night numerical channel for the future spectrogram. Only the first {PREVIEW_SAMPLES} decoded samples are shown; NaN marks missing data.</p>
    {capabilities.isPending && <p role="status"><Spinner /> Loading channel selectors…</p>}
    {capabilities.isError && <Alert role="alert">Channel selectors could not be loaded.</Alert>}
    {capabilities.isSuccess && !channels.length && <p>No numerical channels available.</p>}
    <label>Channel selector <select aria-label="Channel selector" value={selector} disabled={loading || !channels.length}
      onChange={(event) => { setSelection(event.target.value); setPreview(null); setError(""); }}>
      {channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.label} ({channel.id})</option>)}
    </select></label>{" "}
    <Button type="button" disabled={loading || !selector || capabilities.isError} onClick={() => void fetchChannel()}>Fetch full channel</Button>
    {loading && <p role="status"><Spinner /> Fetching and parsing full channel…</p>}
    {error && <Alert role="alert">{error}</Alert>}
    {preview && <>
      <dl className="operational-summary-strip">
        <div><dt>Selector</dt><dd>{preview.channel}</dd></div>
        <div><dt>Sample rate</dt><dd>{preview.sampleRateHz} Hz</dd></div>
        <div><dt>Sample count</dt><dd>{preview.sampleCount}</dd></div>
        <div><dt>Unit</dt><dd>{preview.unit}</dd></div>
        <div><dt>Payload size</dt><dd>{(preview.bytes / 1_000_000).toFixed(3)} MB</dd></div>
        <div><dt>Fetch / parse time</dt><dd>{preview.elapsedMs.toFixed(1)} ms</dd></div>
      </dl>
      <pre aria-label="Decoded sample preview">{preview.values}</pre>
    </>}
  </section>;
}
